import appI18nInstance from 'i18n';
import _get from 'lodash/get';
import {
  SubscriptionPlanNameEnum,
  VATTypeEnum,
  type IUser,
} from 'types/users.types';

export type PricingPlansData = {
  plans: SubscriptionPlanNameEnum[];
  defaultInstallment: 1 | 12;
  extraPlans: SubscriptionPlanNameEnum[];
  recommendedPlan?: SubscriptionPlanNameEnum;
  disclaimer: {
    monthly: string;
    yearly: string;
  };
  options?: {
    hideInstallmentToggle?: boolean;
    disableInstallmentFiltering?: boolean;
  };
  features?: {
    maxNumberOfConnectors?: number;
    maxNumberOfCreditCards?: number;
  };
  blockVATChange?: (target: VATTypeEnum) => boolean;
  requiredPlanToAllowVATChange?: SubscriptionPlanNameEnum;
};

const nonVATPlans: Record<string, PricingPlansData> = {
  [SubscriptionPlanNameEnum.free]: {
    plans: [
      SubscriptionPlanNameEnum.small1,
      SubscriptionPlanNameEnum.small12,
      SubscriptionPlanNameEnum.small_max1,
      SubscriptionPlanNameEnum.small_max12,
    ],
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.free_trial',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.free_trial',
      ),
    },
    defaultInstallment: 12,
    extraPlans: [],
    recommendedPlan: SubscriptionPlanNameEnum.pro12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.trial1]: {
    plans: [
      SubscriptionPlanNameEnum.small1,
      SubscriptionPlanNameEnum.small12,
      SubscriptionPlanNameEnum.small_max1,
      SubscriptionPlanNameEnum.small_max12,
    ],
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.free_trial',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.free_trial',
      ),
    },
    defaultInstallment: 12,
    extraPlans: [],
    recommendedPlan: SubscriptionPlanNameEnum.pro12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.small1]: {
    plans: [
      SubscriptionPlanNameEnum.small1,
      SubscriptionPlanNameEnum.small12,
      SubscriptionPlanNameEnum.small_max1,
      SubscriptionPlanNameEnum.small_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.small1.monthly',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.small1.yearly',
      ),
    },
    extraPlans: [SubscriptionPlanNameEnum.pro1, SubscriptionPlanNameEnum.pro12],
    recommendedPlan: SubscriptionPlanNameEnum.small_max12,
    blockVATChange: (target: VATTypeEnum) =>
      target === VATTypeEnum.subjectToVAT,
    requiredPlanToAllowVATChange: SubscriptionPlanNameEnum.pro12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.small12]: {
    plans: [
      SubscriptionPlanNameEnum.small12,
      SubscriptionPlanNameEnum.small_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.small12',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.small12',
      ),
    },
    extraPlans: [SubscriptionPlanNameEnum.pro12],
    recommendedPlan: SubscriptionPlanNameEnum.small_max12,
    blockVATChange: (target: VATTypeEnum) =>
      target === VATTypeEnum.subjectToVAT,
    requiredPlanToAllowVATChange: SubscriptionPlanNameEnum.pro12,
    options: { hideInstallmentToggle: true },
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.small_max1]: {
    plans: [
      SubscriptionPlanNameEnum.small_max1,
      SubscriptionPlanNameEnum.small_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.small_max1.monthly',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.small_max1.yearly',
      ),
    },
    extraPlans: [
      SubscriptionPlanNameEnum.pro_max1,
      SubscriptionPlanNameEnum.pro_max12,
    ],
    recommendedPlan: SubscriptionPlanNameEnum.pro_max12,
    options: {},
    blockVATChange: (target: VATTypeEnum) =>
      target === VATTypeEnum.subjectToVAT,
    requiredPlanToAllowVATChange: SubscriptionPlanNameEnum.pro_max12,
    features: {
      maxNumberOfConnectors: 10,
      maxNumberOfCreditCards: 10,
    },
  },
  [SubscriptionPlanNameEnum.small_max12]: {
    plans: [SubscriptionPlanNameEnum.small_max12],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.small_max12.yearly',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.franchisee.small_max12.yearly',
      ),
    },
    extraPlans: [SubscriptionPlanNameEnum.pro_max12],
    options: { hideInstallmentToggle: true },
    recommendedPlan: SubscriptionPlanNameEnum.pro_max12,
    blockVATChange: (target: VATTypeEnum) =>
      target === VATTypeEnum.subjectToVAT,
    requiredPlanToAllowVATChange: SubscriptionPlanNameEnum.pro_max12,
    features: {
      maxNumberOfConnectors: 10,
      maxNumberOfCreditCards: 10,
    },
  },
  [SubscriptionPlanNameEnum.pro1]: {
    plans: [
      SubscriptionPlanNameEnum.pro1,
      SubscriptionPlanNameEnum.pro12,
      SubscriptionPlanNameEnum.pro_max1,
      SubscriptionPlanNameEnum.pro_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.pro1.monthly',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.pro1.yearly',
      ),
    },
    extraPlans: [],
    recommendedPlan: SubscriptionPlanNameEnum.pro_max12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.pro12]: {
    plans: [SubscriptionPlanNameEnum.pro12, SubscriptionPlanNameEnum.pro_max12],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t('subscriptions_screen.disclaimers.pro12'),
      monthly: appI18nInstance.t('subscriptions_screen.disclaimers.pro12'),
    },
    extraPlans: [],
    options: { hideInstallmentToggle: true },
    recommendedPlan: SubscriptionPlanNameEnum.pro_max12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.pro_max1]: {
    plans: [
      SubscriptionPlanNameEnum.pro_max1,
      SubscriptionPlanNameEnum.pro_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t('subscriptions_screen.disclaimers.pro_max1'),
      monthly: appI18nInstance.t('subscriptions_screen.disclaimers.pro_max1'),
    },
    extraPlans: [],
    options: { hideInstallmentToggle: true, disableInstallmentFiltering: true },
    recommendedPlan: SubscriptionPlanNameEnum.pro_max12,
    features: {
      maxNumberOfConnectors: 10,
      maxNumberOfCreditCards: 10,
    },
  },
  [SubscriptionPlanNameEnum.pro_max12]: {
    plans: [],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t('subscriptions_screen.disclaimers.pro_max1'),
      monthly: appI18nInstance.t('subscriptions_screen.disclaimers.pro_max1'),
    },
    extraPlans: [],
    options: {},
    features: {
      maxNumberOfConnectors: 10,
      maxNumberOfCreditCards: 10,
    },
  },
  [SubscriptionPlanNameEnum.ambassador]: {
    plans: [],
    defaultInstallment: 12,
    disclaimer: {
      yearly: '',
      monthly: '',
    },
    extraPlans: [],
    options: {},
    features: {
      maxNumberOfConnectors: 10,
      maxNumberOfCreditCards: 10,
    },
  },
};

const VATPlans = {
  [SubscriptionPlanNameEnum.free]: {
    plans: [
      SubscriptionPlanNameEnum.pro1,
      SubscriptionPlanNameEnum.pro12,
      SubscriptionPlanNameEnum.pro_max1,
      SubscriptionPlanNameEnum.pro_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.vat.free_trial',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.vat.free_trial',
      ),
    },
    extraPlans: [],
    recommendedPlan: SubscriptionPlanNameEnum.pro12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.trial1]: {
    plans: [
      SubscriptionPlanNameEnum.pro1,
      SubscriptionPlanNameEnum.pro12,
      SubscriptionPlanNameEnum.pro_max1,
      SubscriptionPlanNameEnum.pro_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.vat.free_trial',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.vat.free_trial',
      ),
    },
    extraPlans: [],
    recommendedPlan: SubscriptionPlanNameEnum.pro12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.pro1]: {
    plans: [
      SubscriptionPlanNameEnum.pro1,
      SubscriptionPlanNameEnum.pro12,
      SubscriptionPlanNameEnum.pro_max1,
      SubscriptionPlanNameEnum.pro_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.pro1.monthly',
      ),
      monthly: appI18nInstance.t(
        'subscriptions_screen.disclaimers.pro1.yearly',
      ),
    },
    extraPlans: [],
    recommendedPlan: SubscriptionPlanNameEnum.pro_max12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.pro12]: {
    plans: [SubscriptionPlanNameEnum.pro12, SubscriptionPlanNameEnum.pro_max12],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t('subscriptions_screen.disclaimers.pro12'),
      monthly: appI18nInstance.t('subscriptions_screen.disclaimers.pro12'),
    },
    extraPlans: [],
    options: { hideInstallmentToggle: true },
    recommendedPlan: SubscriptionPlanNameEnum.pro_max12,
    features: {
      maxNumberOfConnectors: 5,
      maxNumberOfCreditCards: 5,
    },
  },
  [SubscriptionPlanNameEnum.pro_max1]: {
    plans: [
      SubscriptionPlanNameEnum.pro_max1,
      SubscriptionPlanNameEnum.pro_max12,
    ],
    defaultInstallment: 12,
    disclaimer: {
      yearly: appI18nInstance.t('subscriptions_screen.disclaimers.pro_max1'),
      monthly: appI18nInstance.t('subscriptions_screen.disclaimers.pro_max1'),
    },
    extraPlans: [],
    options: { hideInstallmentToggle: true, disableInstallmentFiltering: true },
    recommendedPlan: SubscriptionPlanNameEnum.pro_max12,
    features: {
      maxNumberOfConnectors: 10,
      maxNumberOfCreditCards: 10,
    },
  },
  [SubscriptionPlanNameEnum.pro_max12]: {
    plans: [],
    defaultInstallment: 12,
    features: {
      maxNumberOfConnectors: 10,
      maxNumberOfCreditCards: 10,
    },
  },
  [SubscriptionPlanNameEnum.ambassador]: {
    plans: [],
    defaultInstallment: 12,
    disclaimer: {
      yearly: '',
      monthly: '',
    },
    extraPlans: [],
    options: {},
    features: {
      maxNumberOfConnectors: 10,
      maxNumberOfCreditCards: 10,
    },
  },
};

const pricingPlansData = {
  [VATTypeEnum.franchisee]: nonVATPlans,
  [VATTypeEnum.exempt]: nonVATPlans,
  [VATTypeEnum.exonerated]: nonVATPlans,
  [VATTypeEnum.subjectToVAT]: VATPlans,
};

export default (
  user: IUser,
  currentPlanInternalName: SubscriptionPlanNameEnum,
): PricingPlansData =>
  _get(
    pricingPlansData,
    [
      user?.VATType as VATTypeEnum,
      currentPlanInternalName as SubscriptionPlanNameEnum,
    ],
    {},
  );
